import React, { useState, useEffect } from 'react';
import SEOHead from '../components/SEOHead';
import { GlassCard, KpiTile as BrandKpiTile, EnforcedText } from '../components/shared';
import LoadingSpinner from '../components/LoadingSpinner';
import ComparisonPill from '../components/ComparisonPill';
import ExportButton from '../components/ExportButton';
import DataTypeChip from '../components/DataTypeChip';
import { DollarSign, Zap, Filter, Search } from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PricingMatrix = () => {
  const [loading, setLoading] = useState(true);
  const [variants, setVariants] = useState([]);
  const [filterType, setFilterType] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedWattage, setSelectedWattage] = useState('');
  const [availableWattages, setAvailableWattages] = useState({ AC: [], DC: [] });
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Check if user has admin role
  const isAdmin = user?.role === 'super_admin' || user?.role === 'program_admin';

  // Fetch available wattages on mount
  useEffect(() => {
    fetchWattages();
  }, []);

  // Fetch variants when filters change
  useEffect(() => {
    fetchVariants();
  }, [filterType, selectedWattage]);

  const fetchWattages = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/pricing/wattages`);
      setAvailableWattages(response.data);
    } catch (error) {
      console.error('Error fetching wattages:', error);
    }
  };

  const fetchVariants = async () => {
    try {
      setLoading(true);
      let url = `${BACKEND_URL}/api/pricing/variants?`;
      if (filterType) url += `type=${filterType}&`;
      if (selectedWattage) {
        // Filter by exact power match
        url += `power_kw=${selectedWattage}&`;
      }
      if (searchQuery) url += `q=${searchQuery}&`;
      
      const response = await axios.get(url);
      setVariants(response.data.variants || []);
    } catch (error) {
      console.error('Error fetching pricing variants:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (value >= 10000000) {
      return `₹${(value / 10000000).toFixed(2)}Cr`;
    } else if (value >= 100000) {
      return `₹${(value / 100000).toFixed(2)}L`;
    }
    return `₹${value.toLocaleString()}`;
  };

  const handleSearch = () => {
    fetchVariants();
  };

  const handleViewDetail = (skuId) => {
    navigate(`/pricing/${skuId}`);
  };

  // Calculate summary stats
  const totalVariants = variants.length;
  const greenCount = variants.filter(v => v.computed.comparison_status === 'green').length;
  const avgMargin = variants.length > 0 
    ? variants.reduce((sum, v) => sum + v.computed.target_margin_percent, 0) / variants.length 
    : 0;

  if (loading && variants.length === 0) {
    return <LoadingSpinner size="lg" text="Loading pricing matrix..." />;
  }

  return (
    <>
      <SEOHead 
        title="Pricing Matrix - Dynamic Pricing Management"
        description="Manage charging station pricing with SKU variants, AC/DC rates, time-of-day pricing, and location-based price optimization."
        path="/pricing"
        type="WebApplication"
        noIndex={true}
      />
      <div className="space-y-6" data-testid="pricing-matrix">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <DollarSign className="text-purple-600" size={32} />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Pricing Matrix</h1>
            <p className="text-sm text-gray-500 mt-1">
              <EnforcedText>AC & DC charging station pricing • BOM, installation & market comparison</EnforcedText>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <ExportButton 
            endpoint="/api/pricing/export"
            filename="pricing_matrix_export.csv"
            label="Export Matrix"
          />
          <button 
            onClick={fetchVariants}
            className="px-4 py-2 text-sm font-medium bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 rounded-2xl transition-colors shadow-sm"
          >
            Refresh Data
          </button>
        </div>
      </div>

      {/* KPI Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <BrandKpiTile
          title="Total Variants"
          value={totalVariants}
          delta={`${variants.filter(v => v.type === 'AC').length} AC • ${variants.filter(v => v.type === 'DC').length} DC`}
          accentBar="primary"
          definition="Total pricing configurations"
        />
        <BrandKpiTile
          title="Competitive Pricing"
          value={greenCount}
          delta={`${((greenCount / totalVariants) * 100).toFixed(0)}%`}
          subcopy="Below market average"
          accentBar="green"
          definition="Variants priced below market"
        />
        <BrandKpiTile
          title="Avg Target Margin"
          value={`${avgMargin.toFixed(1)}%`}
          subcopy="Across all variants"
          accentBar="accent"
          definition="Average margin at target price"
        />
        <BrandKpiTile
          title="Power Range"
          value={`${Math.min(...variants.map(v => v.power_kw))} - ${Math.max(...variants.map(v => v.power_kw))}`}
          unit="kW"
          subcopy="Full portfolio coverage"
          accentBar="bharat"
          definition="Min to max power capacity"
        />
      </div>

      {/* Filters */}
      <GlassCard title="Filters" subtitle="Filter pricing variants by type, wattage tier, or search">
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="text-xs text-gray-600 block mb-1">Type</label>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setFilterType('');
                  setSelectedWattage('');
                }}
                className={`px-3 py-2 text-sm rounded-lg ${!filterType ? 'bg-purple-100 text-purple-800 border border-purple-200' : 'bg-gray-100 text-gray-600 border border-gray-200'}`}
              >
                All
              </button>
              <button
                onClick={() => {
                  setFilterType('AC');
                  setSelectedWattage('');
                }}
                className={`px-3 py-2 text-sm rounded-lg ${filterType === 'AC' ? 'bg-purple-100 text-purple-800 border border-purple-200' : 'bg-gray-100 text-gray-600 border border-gray-200'}`}
              >
                AC Only
              </button>
              <button
                onClick={() => {
                  setFilterType('DC');
                  setSelectedWattage('');
                }}
                className={`px-3 py-2 text-sm rounded-lg ${filterType === 'DC' ? 'bg-purple-100 text-purple-800 border border-purple-200' : 'bg-gray-100 text-gray-600 border border-gray-200'}`}
              >
                DC Only
              </button>
            </div>
          </div>

          <div>
            <label className="text-xs text-gray-600 block mb-1">Wattage (kW)</label>
            <select
              value={selectedWattage}
              onChange={(e) => setSelectedWattage(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 w-40 bg-white"
              disabled={!filterType}
            >
              <option value="">All Wattages</option>
              {filterType && availableWattages[filterType]?.wattages?.map(kw => (
                <option key={kw} value={kw}>{kw} kW</option>
              ))}
            </select>
            {!filterType && (
              <p className="text-xs text-gray-400 mt-1">Select type first</p>
            )}
          </div>

          <div className="flex-1">
            <label className="text-xs text-gray-600 block mb-1">Search</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Search by SKU or connector..."
                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <button
                onClick={handleSearch}
                className="px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                <Search size={16} />
              </button>
            </div>
          </div>
        </div>
      </GlassCard>

      {/* Pricing Matrix Table */}
      <GlassCard 
        title="Pricing Variants" 
        subtitle={`${variants.length} variants displayed`}
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-700">Type / Power</th>
                <th className="px-4 py-3 text-left font-medium text-gray-700">SKU</th>
                <th className="px-4 py-3 text-right font-medium text-gray-700">Market Avg (₹)</th>
                {isAdmin && <th className="px-4 py-3 text-right font-medium text-gray-700">G4G Cost (₹)</th>}
                <th className="px-4 py-3 text-right font-medium text-gray-700">Selling Band (₹)</th>
                {isAdmin && <th className="px-4 py-3 text-right font-medium text-gray-700">Margin @ Target</th>}
                <th className="px-4 py-3 text-center font-medium text-gray-700">Comparison</th>
                <th className="px-4 py-3 text-center font-medium text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {variants.map((variant, idx) => (
                <tr 
                  key={variant.sku_id} 
                  className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                    variant.type === 'AC' ? 'bg-blue-50/30' : 'bg-orange-50/30'
                  }`}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                        variant.type === 'AC' 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'bg-orange-100 text-orange-800'
                      }`}>
                        {variant.type}
                      </span>
                      <span className="font-semibold">{variant.power_kw} kW</span>
                    </div>
                    <div className="text-xs text-gray-500 mb-1">
                      {variant.connectors.join(', ')}
                    </div>
                    <DataTypeChip type="system" size="xs" />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs font-medium">{variant.sku_id}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="space-y-1">
                      <div className="text-xs text-gray-500">HW: {formatCurrency(variant.computed.market_avg_hw)}</div>
                      <div className="text-xs text-gray-500">Install: {formatCurrency(variant.computed.market_avg_install)}</div>
                      <div className="font-semibold">{formatCurrency(variant.computed.market_avg_total)}</div>
                      <DataTypeChip type="manual" size="xs" />
                    </div>
                  </td>
                  {isAdmin && (
                    <td className="px-4 py-3 text-right">
                      <div className="space-y-1">
                        <div className="text-xs text-gray-500">BOM: {formatCurrency(variant.computed.cost_bom_sum)}</div>
                        <div className="text-xs text-gray-500">Install: {formatCurrency(variant.computed.cost_install_sum)}</div>
                        <div className="font-semibold text-purple-600">{formatCurrency(variant.computed.g4g_total_cost)}</div>
                      </div>
                    </td>
                  )}
                  <td className="px-4 py-3 text-right">
                    <div className="space-y-1">
                      <div className="text-xs text-gray-500">Min: {formatCurrency(variant.selling_band.min)}</div>
                      <div className="font-semibold text-green-600">{formatCurrency(variant.selling_band.target)}</div>
                      <div className="text-xs text-gray-500">Max: {formatCurrency(variant.selling_band.max)}</div>
                    </div>
                  </td>
                  {isAdmin && (
                    <td className="px-4 py-3 text-right">
                      <div className="font-semibold">{formatCurrency(variant.computed.target_margin)}</div>
                      <div className={`text-xs mb-1 ${
                        variant.computed.target_margin_percent >= 0 
                          ? 'text-green-600' 
                          : 'text-red-600'
                      }`}>
                        {variant.computed.target_margin_percent.toFixed(1)}%
                      </div>
                      <DataTypeChip type="derived" size="xs" />
                    </td>
                  )}
                  <td className="px-4 py-3 text-center">
                    <div className="flex justify-center">
                      <ComparisonPill 
                        status={variant.computed.comparison_status}
                        text={variant.computed.comparison_text}
                        size="sm"
                      />
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => handleViewDetail(variant.sku_id)}
                      className="px-3 py-1 text-xs font-medium text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded-lg transition-colors"
                    >
                      {isAdmin ? 'View / Edit' : 'View'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {variants.length === 0 && !loading && (
          <div className="text-center py-12 text-gray-500">
            <p className="text-lg font-medium">No pricing variants found</p>
            <p className="text-sm mt-2">Try adjusting your filters</p>
          </div>
        )}
      </GlassCard>

      {/* Footer Note */}
      <div className="text-center text-xs text-gray-500 py-4 border-t border-gray-200">
        <p>
          <span className="font-semibold">Definition:</span> Pricing based on componentized BOM + installation costs vs. market competitor quotes • 
          <span className="font-semibold"> Source:</span> Internal BOM data & competitor proformas • 
          <span className="font-semibold"> Timestamp:</span> {new Date().toLocaleString()} • 
          <span className="font-semibold text-bharat"> Made in Bharat 🇮🇳</span>
        </p>
      </div>
      </div>
    </>
  );
};

export default PricingMatrix;
